import { useEffect, useRef, useState } from "react"

import { Card, Checkbox, Flex, Form, Input, Layout, Spin, Typography } from "antd"
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from "react-redux"
import { useParams } from "react-router"

import ButtonComponent from "../../Components/Button"
import InputComponent from "../../Components/Input"
import LoginHeader from "../../Components/LoginHeader"
import PrivacyPolicy from "../../Components/PrivacyPolicy"
import { authErrorReset } from "../LoginPage/redux/auth.actions"
import { verify2FA } from "../LoginPage/redux/auth.thunks"

import "./styles.less"

const { Text } = Typography
const AUTH_CODE_LENGTH = 6
const AUTH_CODE_ERROR = "Two-factor code verification failed. Please try again."
const RECOVERY_CODE_ERROR =
  "The two-factor recovery code was entered incorrectly. Please try again."

function AdminTwoFactorVerificationPage() {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { token } = useParams()
  const fetching = useSelector((state) => state.auth.fetching)
  const error = useSelector((state) => state.auth.error)
  const ephemeralToken = useSelector((state) => state.auth.ephemeralToken)
  const [form] = Form.useForm()
  const [mode, setMode] = useState("authCode")
  const [verificationError, setVerificationError] = useState("")
  const [authCodeDigits, setAuthCodeDigits] = useState(Array(AUTH_CODE_LENGTH).fill(""))
  const inputRefs = useRef([])
  const codeValue = Form.useWatch("code", form)
  const isRecoveryCodeMode = mode === "recoveryCode"
  const verificationEphemeralToken = token || ephemeralToken

  const clearVerificationError = () => {
    if (!verificationError) return

    setVerificationError("")
    form.setFields([{ name: "code", errors: [] }])
    if (error) {
      dispatch(authErrorReset())
    }
  }

  const setCodeFieldError = (message) => {
    setVerificationError(message)
    form.setFields([{ name: "code", errors: [message] }])
  }

  const getBackendErrorMessage = () => {
    if (!error) return ""

    const backendDetail = error?.message?.detail || error?.detail || error?.message

    if (typeof backendDetail === "string" && backendDetail.trim()) {
      return backendDetail
    }

    return isRecoveryCodeMode ? RECOVERY_CODE_ERROR : AUTH_CODE_ERROR
  }

  const handleModeChange = (nextMode) => {
    setMode(nextMode)
    setVerificationError("")
    setAuthCodeDigits(Array(AUTH_CODE_LENGTH).fill(""))
    form.setFieldsValue({ code: "" })
    form.setFields([{ name: "code", errors: [] }])
    if (error) {
      dispatch(authErrorReset())
    }
  }

  const handleAuthCodeChange = (index, event) => {
    const digit = event.target.value.replace(/\D/g, "").slice(-1)
    const nextDigits = [...authCodeDigits]

    nextDigits[index] = digit
    setAuthCodeDigits(nextDigits)
    form.setFieldValue("code", nextDigits.join(""))
    clearVerificationError()

    if (digit && index < AUTH_CODE_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus?.()
    }
  }

  const handleRecoveryCodeChange = (event) => {
    form.setFieldValue("code", event.target.value.toUpperCase())
    clearVerificationError()
  }

  const handleAuthCodeKeyDown = (index, event) => {
    if (event.key === "Backspace" && !authCodeDigits[index] && index > 0) {
      inputRefs.current[index - 1]?.focus?.()
    }
  }

  const handleFinish = (values) => {
    if (!verificationEphemeralToken) return

    if (isRecoveryCodeMode) {
      dispatch(
        verify2FA({
          ephemeral_token: verificationEphemeralToken,
          recovery_code: values.code.trim().toUpperCase(),
          trust_device: values.remember_device
        })
      )
      return
    }

    dispatch(
      verify2FA({
        ephemeral_token: verificationEphemeralToken,
        otp: values.code,
        trust_device: values.remember_device
      })
    )
  }

  useEffect(() => {
    document.title = "Admin 2FA Verification - Chargeback"
  }, [])

  useEffect(() => {
    const message = getBackendErrorMessage()

    if (!message) return

    setCodeFieldError(message)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, error, isRecoveryCodeMode])

  useEffect(
    () => () => {
      dispatch(authErrorReset())
    },
    [dispatch]
  )

  return (
    <Layout>
      <Spin size="large" spinning={fetching}>
        <div className="login-row">
          <LoginHeader withLogout />
          <div className="login-content">
            <div className="content-main sign-up">
              <Card className="login-card">
                <h2 className="card-title" style={{ marginBottom: 12 }}>
                  {isRecoveryCodeMode ? t("Two-factor recovery") : t("Two-factor authentication")}
                </h2>

                <h4 className="card-subtitle-signup" style={{ marginBottom: 24 }}>
                  {isRecoveryCodeMode
                    ? "If you are unable to access your device or cannot receive a two-factor authentication code, enter one of your recovery codes to verify your identity."
                    : "Enter the code from your two-factor authentication app or browser extension below."}
                </h4>

                <Form
                  form={form}
                  layout="vertical"
                  requiredMark={false}
                  className="login-form"
                  onFinish={handleFinish}
                  initialValues={{
                    remember_device: true,
                    code: ""
                  }}
                >
                  {isRecoveryCodeMode ? (
                    <>
                      <Form.Item
                        name="code"
                        validateStatus={verificationError ? "error" : undefined}
                        rules={[{ required: true, message: t("OTP is required") }]}
                      >
                        <Input
                          size="large"
                          placeholder="XXXXX-XXXXX"
                          autoComplete="one-time-code"
                          onChange={handleRecoveryCodeChange}
                          status={verificationError ? "error" : undefined}
                        />
                      </Form.Item>
                      {verificationError ? (
                        <Text type="danger" className="admin-2fa-error-text">
                          {verificationError}
                        </Text>
                      ) : null}
                    </>
                  ) : (
                    <>
                      <Form.Item
                        name="code"
                        hidden
                        rules={[
                          { required: true, message: t("OTP is required") },
                          {
                            len: AUTH_CODE_LENGTH,
                            message: t("OTP is required")
                          }
                        ]}
                      >
                        <Input />
                      </Form.Item>

                      <Form.Item
                        validateStatus={verificationError ? "error" : undefined}
                        className="admin-2fa-auth-code-item"
                      >
                        <Flex gap={12}>
                          {Array.from({ length: AUTH_CODE_LENGTH }).map((_, index) => (
                            <InputComponent
                              key={index}
                              ref={(node) => {
                                inputRefs.current[index] = node
                              }}
                              className="security-2fa-code-input"
                              maxLength={1}
                              value={authCodeDigits[index]}
                              onChange={(event) => handleAuthCodeChange(index, event)}
                              onKeyDown={(event) => handleAuthCodeKeyDown(index, event)}
                              status={verificationError ? "error" : undefined}
                            />
                          ))}
                        </Flex>
                      </Form.Item>
                      {verificationError ? (
                        <Text type="danger" className="admin-2fa-error-text">
                          {verificationError}
                        </Text>
                      ) : null}
                    </>
                  )}

                  <Form.Item name="remember_device" valuePropName="checked" initialValue>
                    <Checkbox>{t("Remember this trusted device for 30 days")}</Checkbox>
                  </Form.Item>

                  <ButtonComponent
                    type="primary"
                    size="large"
                    block
                    htmlType="submit"
                    disabled={!codeValue || fetching}
                  >
                    {t("Verify")}
                  </ButtonComponent>
                </Form>

                {isRecoveryCodeMode ? (
                  <ButtonComponent
                    type="primary"
                    size="large"
                    block
                    className="admin-2fa-recovery-btn"
                    onClick={() => handleModeChange("authCode")}
                  >
                    {t("Use 2FA code")}
                  </ButtonComponent>
                ) : (
                  <ButtonComponent
                    type="primary"
                    size="large"
                    block
                    className="admin-2fa-recovery-btn"
                    onClick={() => handleModeChange("recoveryCode")}
                  >
                    {t("Use recovery code")}
                  </ButtonComponent>
                )}
              </Card>

              <PrivacyPolicy />
            </div>
          </div>
        </div>
      </Spin>
    </Layout>
  )
}

export default AdminTwoFactorVerificationPage
